import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { COOKIE_NAMES } from './constants.ts';
import { getCookieData } from './cookies.ts';
import { MARKETS } from './markets.ts';
import { TCurrency } from '../types/types';

countries.registerLocale(enLocale);

export const MARKETS_CODES = {
  ___en: '___en',
  ca_en: 'ca_en',
  fi_fi: 'fi_fi',
  gb_en: 'gb_en',
  in_en: 'in_en',
  no_no: 'no_no',
  on_en: 'on_en',
  se_sv: 'se_sv',
  es_es: 'es_es',
  nz_en: 'nz_en',
  jp_ja: 'jp_ja',
  gi_en: 'gi_en',
  ie_en: 'ie_en',
  de_de: 'de_de',
  at_de: 'at_de',
} as const;

export const FLOW_NAMES = Object.freeze({
  GRA:         'gra',
  MGA_GRA_PNP: 'mga_gra_pnp',
  UKGC:        'uk_gc',
  SWEDEN:      'sweden',
  AGCO:        'agco',
  DGOJ:        'dgoj',
  MGA:         'mga',
} as const);

export const JURISDICTION = Object.freeze({
  SGA:  'SGA',
  MGA:  'MGA',
  GRA:  'GRA',
  UKGC: 'UKGC',
  DGOJ: 'DGOJ',
} as const);

export const CA_PROVINCES = [
  {
    name: 'Alberta',
    code: 'AB',
  },
  {
    name: 'British Columbia',
    code: 'BC',
  },
  {
    name: 'Manitoba',
    code: 'MB',
  },
  {
    name: 'New Brunswick',
    code: 'NB',
  },
  {
    name: 'Newfoundland and Labrador',
    code: 'NL',
  },
  {
    name: 'Nova Scotia',
    code: 'NS',
  },
  {
    name: 'Ontario',
    code: 'ON',
  },
  {
    name: 'Prince Edward Island',
    code: 'PE',
  },
  {
    name: 'Quebec',
    code: 'QC',
  },
  {
    name: 'Saskatchewan',
    code: 'SK',
  },
  {
    name: 'Northwest Territories',
    code: 'NT',
  },
  {
    name: 'Nunavut',
    code: 'NU',
  },
  {
    name: 'Yukon',
    code: 'YT',
  },
] as const;

export type CAProvince = (typeof CA_PROVINCES)[number];
export type CAProvinceCode = CAProvince['code'];

// These provinces are currently disabled due to regulatory requirements - TEXP-4839
export const DISABLED_CA_PROVINCES = [
  'AB',
  'ON',
] as const satisfies readonly CAProvinceCode[];

export type TCountryMarketInfo = {
  countryCode: string;
  market: string;
  flowName: string;
  language: string;
  jurisdiction?: string;
  marketCode?: string;
  currency?: TCurrency;
  phonePrefix?: string;
  // When true, residency for this market is fixed to this entry's countryCode.
  // Consumers must ignore cookie/geo guesses (a stale value e.g. from an office
  // VPN would otherwise poison form defaults and break market lookup).
  pinCountry?: boolean;
}

export const countryMarketInfoMapper: TCountryMarketInfo[] = [
  {
    countryCode:  'NZ',
    market:       MARKETS.ENNZ,
    marketCode:   MARKETS_CODES.nz_en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-NZ',
    jurisdiction: JURISDICTION.GRA,
    phonePrefix:  '+64',
    currency:     'NZD',
  },
  {
    countryCode:  'IE',
    market:       MARKETS.GI,
    marketCode:   MARKETS_CODES.gi_en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-GI',
    jurisdiction: JURISDICTION.GRA,
    currency:     'EUR',
    phonePrefix:  '+353',
  },
  {
    countryCode:  'GI',
    market:       MARKETS.GI,
    marketCode:   MARKETS_CODES.gi_en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-GI',
    jurisdiction: JURISDICTION.GRA,
    currency:     'EUR',
    phonePrefix:  '+350',
  },
  {
    countryCode:  'ES',
    market:       MARKETS.ES,
    marketCode:   MARKETS_CODES.es_es,
    flowName:     FLOW_NAMES.DGOJ,
    language:     'es-ES',
    jurisdiction: JURISDICTION.DGOJ,
    currency:     'EUR',
    phonePrefix:  '+34',
    pinCountry:   true,
  },
  {
    countryCode:  'NO',
    market:       MARKETS.GI,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-GI',
    jurisdiction: JURISDICTION.GRA,
  },
  {
    countryCode:  'JE',
    market:       MARKETS.GI,
    marketCode:   MARKETS_CODES.gi_en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-GI',
    jurisdiction: JURISDICTION.GRA,
    currency:     'EUR',
    phonePrefix:  '+44',
  },
  {
    countryCode:  'IM',
    market:       MARKETS.GI,
    marketCode:   MARKETS_CODES.gi_en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-GI',
    jurisdiction: JURISDICTION.GRA,
    currency:     'EUR',
    phonePrefix:  '+44-1624',
  },
  {
    countryCode:  'BM',
    market:       MARKETS.GI,
    marketCode:   MARKETS_CODES.gi_en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-GI',
    jurisdiction: JURISDICTION.GRA,
    currency:     'EUR',
    phonePrefix:  '+1 441',
  },
  {
    countryCode:  'MC',
    market:       MARKETS.GI,
    marketCode:   MARKETS_CODES.gi_en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-GI',
    jurisdiction: JURISDICTION.GRA,
    currency:     'EUR',
    phonePrefix:  '+377',
  },
  {
    countryCode:  'PG',
    market:       MARKETS.GI,
    marketCode:   MARKETS_CODES.gi_en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-GI',
    jurisdiction: JURISDICTION.GRA,
    currency:     'EUR',
    phonePrefix:  '+675',
  },
  {
    countryCode:  'LI',
    market:       MARKETS.GI,
    marketCode:   MARKETS_CODES.gi_en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-GI',
    jurisdiction: JURISDICTION.GRA,
    currency:     'EUR',
    phonePrefix:  '+423',
  },
  {
    countryCode:  'MT',
    market:       MARKETS.EN,
    marketCode:   MARKETS_CODES.___en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en',
    jurisdiction: JURISDICTION.MGA,
    currency:     'EUR',
  },
  { countryCode: 'SE', market: MARKETS.SV, flowName: FLOW_NAMES.SWEDEN, language: 'sv-SE' },
  {
    countryCode:  'GB',
    market:       MARKETS.ENGB,
    marketCode:   MARKETS_CODES.gb_en,
    flowName:     FLOW_NAMES.UKGC,
    language:     'en-GB',
    jurisdiction: JURISDICTION.UKGC,
    currency:     'GBP',
    phonePrefix:  '+44',
  },
  {
    countryCode:  'CA',
    market:       MARKETS.ENCA,
    marketCode:   MARKETS_CODES.ca_en,
    flowName:     FLOW_NAMES.GRA,
    language:     'en-CA',
    jurisdiction: JURISDICTION.GRA,
    currency:     'CAD',
    phonePrefix:  '+1',
  },
  {
    countryCode: 'CA',
    market:      MARKETS.ON,
    marketCode:  MARKETS_CODES.on_en,
    flowName:    FLOW_NAMES.AGCO,
    language:    'en-ON',
  },
  {
    countryCode:  'FI',
    market:       MARKETS.FI,
    marketCode:   MARKETS_CODES.fi_fi,
    flowName:     FLOW_NAMES.MGA_GRA_PNP,
    language:     'fi-FI',
    jurisdiction: JURISDICTION.GRA,
    currency:     'EUR',
    phonePrefix:  '+358',
    pinCountry:   true,
  },
  { countryCode: 'DZ', market: MARKETS.ROG, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'LB', market: MARKETS.ROG, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'QA', market: MARKETS.ROG, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'AE', market: MARKETS.ROG, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'KW', market: MARKETS.ROG, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'SA', market: MARKETS.ROG, flowName: FLOW_NAMES.GRA, language: 'en' },
  {
    countryCode:  'AX',
    market:       MARKETS.EN,
    flowName:     FLOW_NAMES.GRA,
    language:     'en',
    jurisdiction: JURISDICTION.MGA,
    currency:     'EUR',
  },
  { countryCode: 'ML', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'PH', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'ID', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'EG', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'VN', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'SB', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'TH', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'BQ', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'CR', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'NA', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'BO', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'KN', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'BB', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'PE', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'ZA', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'UY', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'RW', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MV', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'CU', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'LS', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'AI', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'GT', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'TL', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'CL', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MY', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'TZ', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'TC', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'FJ', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'KP', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'TT', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'LC', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'NR', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'CW', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MD', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'SC', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'SH', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'NU', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'IO', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'CV', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'GD', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'HN', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'SZ', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'DO', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'GW', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MZ', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'IR', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MH', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'ML', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MK', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'SX', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'RU', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MW', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'TK', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'GQ', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'TV', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MG', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'AW', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'KR', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'JM', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'LA', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MS', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'FK', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'BR', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'BW', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'PN', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'VC', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'AM', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'ST', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'VG', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'CI', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'GE', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MU', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'GS', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'FM', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MX', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'ZM', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'KY', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'TO', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'KI', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'CC', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'AR', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
  { countryCode: 'MN', market: MARKETS.ROW, flowName: FLOW_NAMES.GRA, language: 'en' },
] as const;

/**
 * Returns the appropriate country market information based on provided parameters or user's context
 * @param marketSlug - The market slug to find
 * @param countryCode - Optional country code to disambiguate multi-country slugs
 * @returns TCountryMarketInfo - The matching country market info or default Ireland config
 */
export const currentCountryMarketInfo = (marketSlug: string, countryCode?: string): TCountryMarketInfo => {
  // Default fallback configuration for Ireland
  const defaultMarketInfo: TCountryMarketInfo = {
    countryCode:  'GI',
    market:       'gi',
    marketCode:   MARKETS_CODES.gi_en,
    language:     'en-GI',
    flowName:     'gra',
    jurisdiction: JURISDICTION.GRA,
    currency:     'EUR',
  };

  if (!marketSlug) return defaultMarketInfo;

  const matches = countryMarketInfoMapper.filter(c => c.market === marketSlug);
  if (matches.length === 0) return defaultMarketInfo;

  // Country-pinned slugs (FI, ES) ignore the countryCode hint — the mapper entry
  // is the source of truth, so a stale value (e.g. MT from an office VPN cookie)
  // can't drop us into the GI fallback.
  const pinned = matches.find(c => c.pinCountry);
  if (pinned) return pinned;

  return matches.find(c => c.countryCode === countryCode) ?? matches[0];
};

export const acceptedCountryCodes: string[] = [
  'AM',
  'AW',
  'AX',
  'BM',
  'CA',
  'DO',
  'EG',
  'FI',
  'FK',
  'GB',
  'GD',
  'GE',
  'GI',
  'GL',
  'IE',
  'IM',
  'ID',
  'IS',
  'JE',
  'KW',
  'LI',
  'LU',
  'MW',
  'MT',
  'MC',
  'NR',
  'NZ',
  'NO',
  'PG',
  'QA',
  'SM',
  'SB',
  'SE',
  'SJ',
  'SZ',
  'TV',
  'UY',
  'ZM',
] as const;

export const isAndroid = (): boolean =>
  window?.navigator?.userAgent.toLowerCase().includes('android');

export const isAndroidNative = (): boolean => (window?.native && isAndroid()) || false;

const androidNativeExcludedCountries: string[] = [
  'AX', // Aland Islands
  'AM', // Armenia
  'AW', // Aruba
  'DO', // Dominican Republic
  'EG', // Egypt
  'FK', // Falkland Islands
  'GE', // Georgia
  'GI', // Gibraltar
  'GD', // Grenada
  'IS', // Iceland
  'ID', // Indonesia
  'JE', // Jersey
  'KW', // Kuwait
  'LU', // Luxembourg
  'MW', // Malawi
  'MT', // Malta
  'NR', // Nauru
  'QA', // Qatar
  'SM', // San Marino
  'SB', // Solomon Islands
  'SJ', // Svalbard and Jan Mayen
  'TV', // Tuvalu
  'UY', // Uruguay
  'ZM', // Zambia
];

export const COUNTRY_ALIASES: Record<string, string> = {
  Syria:            'Syrian Arab Republic',
  'South Korea':    'Korea, Republic of',
  'North Korea':    'Korea, Democratic People\'s Republic of',
  Russia:           'Russian Federation',
  Iran:             'Iran, Islamic Republic of',
  Moldova:          'Moldova, Republic of',
  Venezuela:        'Venezuela, Bolivarian Republic of',
  Bolivia:          'Bolivia, Plurinational State of',
  Tanzania:         'Tanzania, United Republic of',
  Vietnam:          'Viet Nam',
  Laos:             'Lao People\'s Democratic Republic',
  'Czech Republic': 'Czechia',
  'Cape Verde':     'Cabo Verde',
};

export const closedMarketCountryCodes: string[] = [
  // /row
  'AM', // Armenia
  'AW', // Aruba
  'DO', // Dominican Republic
  'EG', // Egypt
  'FK', // Falkland Islands
  'GE', // Georgia
  'GD', // Grenada
  'ID', // Indonesia
  'MW', // Malawi
  'NR', // Nauru
  'SB', // Solomon Islands
  'SZ', // Eswatini
  'TV', // Tuvalu
  'UY', // Uruguay
  'ZM', // Zambia
  // /en
  'AX', // Åland Islands
  'IS', // Iceland
  'LU', // Luxembourg
  'MT', // Malta
  'SM', // San Marino
  'SJ', // Svalbard and Jan Mayen
  // /rog
  'KW', // Kuwait
  'QA', // Qatar
];

export const getAcceptedCountryCodes = (): string[] => {
  const openMarketCountryCodes = acceptedCountryCodes.filter(
    code => !closedMarketCountryCodes.includes(code),
  );

  if (isAndroidNative()) {
    return openMarketCountryCodes.filter(
      code => !androidNativeExcludedCountries.includes(code),
    );
  }

  return openMarketCountryCodes;
};

type CountryCurrencyMap = Record<string, string>;

export const currencyByCountryCode: CountryCurrencyMap = Object.freeze({
  SE: 'SEK',
  NO: 'NOK',
  FI: 'EUR',
  DE: 'EUR',
  CA: 'CAD',
  DK: 'DKK',
  NZ: 'NZD',
  MT: 'EUR',
  IN: 'INR',
  JP: 'USD',
  AT: 'EUR',
  IE: 'EUR',
  SB: 'USD',
  TH: 'USD',
  PH: 'USD',
  ID: 'USD',
  DZ: 'USD',
  EG: 'USD',
  LB: 'USD',
  QA: 'USD',
  AE: 'USD',
  KW: 'USD',
  VN: 'USD',
  SA: 'USD',
  BQ: 'USD',
  CR: 'USD',
  NA: 'USD',
  BO: 'USD',
  KN: 'USD',
  BB: 'USD',
  PE: 'USD',
  ZA: 'USD',
  UY: 'USD',
  RW: 'USD',
  MV: 'USD',
  CU: 'USD',
  LS: 'USD',
  AI: 'USD',
  GT: 'USD',
  TL: 'USD',
  CL: 'USD',
  MY: 'USD',
  TZ: 'USD',
  TC: 'USD',
  FJ: 'USD',
  KP: 'USD',
  TT: 'USD',
  LC: 'USD',
  NR: 'USD',
  CW: 'USD',
  MD: 'USD',
  SC: 'USD',
  SH: 'USD',
  NU: 'USD',
  IO: 'USD',
  CV: 'USD',
  GD: 'USD',
  HN: 'USD',
  SZ: 'USD',
  DO: 'USD',
  GW: 'USD',
  MZ: 'USD',
  IR: 'USD',
  MH: 'USD',
  ML: 'USD',
  MK: 'USD',
  SX: 'USD',
  RU: 'USD',
  MW: 'USD',
  TK: 'USD',
  GQ: 'USD',
  TV: 'USD',
  MG: 'USD',
  AW: 'USD',
  KR: 'USD',
  JM: 'USD',
  LA: 'USD',
  MS: 'USD',
  FK: 'USD',
  BR: 'USD',
  BW: 'USD',
  JE: 'EUR',
  GG: 'EUR',
  IM: 'EUR',
  PN: 'USD',
  VC: 'USD',
  AM: 'USD',
  ST: 'USD',
  VG: 'USD',
  CI: 'USD',
  GE: 'USD',
  MU: 'USD',
  GS: 'USD',
  FM: 'USD',
  MX: 'USD',
  ZM: 'USD',
  KY: 'USD',
  TO: 'USD',
  KI: 'USD',
  CC: 'USD',
  AR: 'USD',
  MN: 'USD',
  GB: 'GBP',
  ES: 'EUR',
  NL: 'EUR',
});

export const countryCodeAliases = ['AX']

const getUserSelectedCountryCode = (): string | null => {
  const preselectedCountry = sessionStorage.getItem('preselectedCountry');
  if (preselectedCountry) {
    try {
      sessionStorage.removeItem('preselectedCountry');

      return JSON.parse(preselectedCountry).code;

    } catch (error) {
      console.error('Error parsing preselectedCountry from sessionStorage:', error);

      return null;
    }
  }

  return null;
};

// Helper function to find market from user context
export const findCountryCodeFromUserContext = (): string | undefined => {
  const userCountryCode = getUserSelectedCountryCode() ||
    getCookieData(COOKIE_NAMES.KAZUMOKEYCLOAK, 'country');

  if (!userCountryCode) return undefined;

  const entry = countryMarketInfoMapper.find(c => c.countryCode === userCountryCode);

  return entry?.countryCode;
};

// Helper function to check if a province code is in the list of disabled provinces
export const isDisabledProvince = (
  code: CAProvinceCode,
): boolean =>
  DISABLED_CA_PROVINCES.includes(
    code as (typeof DISABLED_CA_PROVINCES)[number],
  );
